/**
 * 用户管理 - 列表 + 搜索 + 筛选 + 详情抽屉 + 操作
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Filter, X, Edit2, Ban, CheckCircle, CreditCard, ChevronLeft, ChevronRight,
  Download, Eye, Phone, Calendar, Activity, Crown
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const TIER_OPTIONS = ['全部', 'Pro 月付', 'Pro 年付', 'MAX 买断'];
const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'active', label: '活跃' },
  { value: 'disabled', label: '已禁用' },
];

export function UsersView() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('全部');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [addPayment, setAddPayment] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (search) params.search = search;
      if (tierFilter !== '全部') params.tier = tierFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await api.admin.users(params);
      setUsers(res.users);
      setTotal(res.total);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [page]);

  function handleSearch() {
    setPage(1);
    load();
  }

  function handleExport() {
    const csv = [
      ['ID', '手机号', '姓名', '会员档', '状态', '注册时间', '最后登录'],
      ...users.map(u => [
        u.id, u.phone, u.name, u.tier, u.status,
        new Date(u.created_at).toLocaleString('zh-CN'),
        u.last_login_at ? new Date(u.last_login_at).toLocaleString('zh-CN') : '-'
      ])
    ].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`导出 ${users.length} 条记录`);
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-2">
      {/* 工具栏 */}
      <div className="card !p-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="flex items-center gap-1.5 flex-1 min-w-[200px] bg-bg-base rounded-md px-2 py-1 border border-white/5">
            <Search size={12} className="text-ink-3" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="搜索手机号 / 姓名"
              className="flex-1 bg-transparent text-xs outline-none"
            />
            {search && (
              <button onClick={() => { setSearch(''); setPage(1); load(); }} className="text-ink-3">
                <X size={10} />
              </button>
            )}
          </div>

          <select
            value={tierFilter}
            onChange={e => { setTierFilter(e.target.value); setPage(1); }}
            className="bg-bg-base border border-white/5 rounded-md px-2 py-1 text-xs"
          >
            {TIER_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-bg-base border border-white/5 rounded-md px-2 py-1 text-xs"
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <button onClick={handleSearch} className="btn-ghost text-[11px] flex items-center gap-1 py-0.5 px-2">
            <Filter size={10} /> 筛选
          </button>

          <div className="flex-1" />

          <button onClick={handleExport} className="btn-ghost text-[11px] flex items-center gap-1 py-0.5 px-2">
            <Download size={10} /> 导出 CSV
          </button>
        </div>
      </div>

      {/* 用户列表 */}
      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/2 text-[10px] text-ink-3">
              <tr>
                <th className="text-left py-2 px-2.5 font-medium">用户</th>
                <th className="text-left py-2 px-2.5 font-medium">手机号</th>
                <th className="text-left py-2 px-2.5 font-medium">会员档</th>
                <th className="text-right py-2 px-2.5 font-medium">直推</th>
                <th className="text-right py-2 px-2.5 font-medium">累计佣金</th>
                <th className="text-left py-2 px-2.5 font-medium">状态</th>
                <th className="text-left py-2 px-2.5 font-medium">注册时间</th>
                <th className="text-right py-2 px-2.5 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-white/3 hover:bg-white/3">
                  <td className="py-1.5 px-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                        {u.name?.[0] || 'U'}
                      </div>
                      <div>
                        <div className="text-xs font-medium">{u.name}</div>
                        <div className="text-[9px] text-ink-3 font-mono">#{u.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-1.5 px-2.5 font-mono text-[11px]">{u.phone}</td>
                  <td className="py-1.5 px-2.5">
                    <TierBadge tier={u.tier} />
                  </td>
                  <td className="py-1.5 px-2.5 text-right font-mono text-[11px] text-brand-cyan">
                    {u.direct_count || 0}
                  </td>
                  <td className="py-1.5 px-2.5 text-right font-mono text-[11px] text-accent-success font-semibold">
                    ¥{Number(u.total_commission || 0).toFixed(2)}
                  </td>
                  <td className="py-1.5 px-2.5">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="py-1.5 px-2.5 text-[11px] text-ink-3">
                    {new Date(u.created_at).toLocaleDateString('zh-CN')}
                  </td>
                  <td className="py-1.5 px-2.5 text-[11px] text-ink-3">
                    {u.last_login_at ? (
                      <div>
                        <div>{new Date(u.last_login_at).toLocaleDateString('zh-CN')}</div>
                        <div className="text-[9px] font-mono opacity-70">{u.last_login_ip}</div>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="py-1.5 px-2.5 text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <button
                        onClick={() => setSelected(u)}
                        className="p-1 rounded hover:bg-white/5 text-ink-2"
                        title="查看详情"
                      >
                        <Eye size={12} />
                      </button>
                      <button
                        onClick={() => setEditing(u)}
                        className="p-1 rounded hover:bg-white/5 text-ink-2"
                        title="编辑"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={() => setAddPayment(u)}
                        className="p-1 rounded hover:bg-white/5 text-brand-gold"
                        title="手动补单"
                      >
                        <CreditCard size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="text-center py-4 text-xs text-ink-3">加载中...</div>}
          {users.length === 0 && !loading && (
            <div className="text-center py-6 text-xs text-ink-3">暂无用户</div>
          )}
        </div>

        {/* 分页 */}
        {total > pageSize && (
          <div className="flex items-center justify-between border-t border-white/5 px-2.5 py-1.5 bg-white/2">
            <div className="text-[10px] text-ink-3">
              共 <span className="text-ink-1 font-mono">{total}</span> 个用户
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-0.5 rounded hover:bg-white/5 disabled:opacity-30"
              >
                <ChevronLeft size={12} />
              </button>
              <div className="text-[10px] px-1.5 font-mono">{page} / {totalPages}</div>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-0.5 rounded hover:bg-white/5 disabled:opacity-30"
              >
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 抽屉 - 用户详情 */}
      <AnimatePresence>
        {selected && <UserDetailDrawer user={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>

      {/* 弹窗 - 编辑 */}
      <AnimatePresence>
        {editing && (
          <EditUserModal
            user={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { load(); setEditing(null); }}
          />
        )}
      </AnimatePresence>

      {/* 弹窗 - 补单 */}
      <AnimatePresence>
        {addPayment && (
          <AddPaymentModal
            user={addPayment}
            onClose={() => setAddPayment(null)}
            onSaved={() => { load(); setAddPayment(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function TierBadge({ tier }) {
  const config = {
    'MAX 买断': { color: 'bg-brand-gold/20 text-brand-gold', icon: Crown },
    'Pro 年付': { color: 'bg-brand-purple/20 text-brand-purple', icon: Crown },
    'Pro 月付': { color: 'bg-brand-cyan/20 text-brand-cyan', icon: Crown },
  }[tier] || { color: 'bg-white/10 text-ink-2', icon: Crown };
  const Icon = config.icon;

  return (
    <span className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium', config.color)}>
      <Icon size={10} />
      {tier}
    </span>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
      status === 'active'
        ? 'bg-brand-cyan/20 text-brand-cyan'
        : 'bg-accent-danger/20 text-accent-danger'
    )}>
      {status === 'active' ? <CheckCircle size={10} /> : <Ban size={10} />}
      {status === 'active' ? '活跃' : '已禁用'}
    </span>
  );
}

function UserDetailDrawer({ user, onClose }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.admin.getUser(user.id).then(setDetail).catch(e => toast.error(e.message));
  }, [user.id]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 z-50"
      />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.2 }}
        className="fixed inset-y-0 right-0 w-full max-w-md bg-bg-card border-l border-white/10 z-50 overflow-y-auto"
      >
        <div className="sticky top-0 bg-bg-card border-b border-white/5 p-4 flex items-center justify-between z-10">
          <h2 className="font-semibold">用户详情</h2>
          <button onClick={onClose} className="text-ink-3 hover:text-ink-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* 头部 */}
          <div className="text-center pb-4 border-b border-white/5">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center text-2xl font-bold mx-auto mb-2">
              {user.name?.[0] || 'U'}
            </div>
            <div className="font-semibold">{user.name}</div>
            <div className="text-xs text-ink-3 font-mono">{user.phone}</div>
            <div className="mt-2 flex items-center justify-center gap-1.5">
              <TierBadge tier={user.tier} />
              <StatusBadge status={user.status} />
            </div>
          </div>

          {!detail ? (
            <div className="text-center text-xs text-ink-3 py-8">加载中...</div>
          ) : (
            <>
              {/* 基础信息 */}
              <Section title="基础信息">
                <InfoRow icon={Phone} label="手机号" value={user.phone} />
                <InfoRow icon={Calendar} label="注册时间" value={new Date(user.created_at).toLocaleString('zh-CN')} />
                <InfoRow icon={Activity} label="最后登录" value={user.last_login_at ? new Date(user.last_login_at).toLocaleString('zh-CN') : '-'} />
                <InfoRow icon={Crown} label="会员档" value={user.tier} />
                {user.trial_expires_at && (
                  <InfoRow icon={Clock} label="试用到期" value={new Date(user.trial_expires_at).toLocaleString('zh-CN')} />
                )}
              </Section>

              {/* 佣金统计 */}
              {detail.commission_stats && (
                <Section title="佣金统计">
                  <div className="grid grid-cols-3 gap-1.5">
                    <div className="bg-bg-base rounded-lg p-2 text-center">
                      <div className="text-[10px] text-brand-cyan">L1 直推</div>
                      <div className="text-sm font-bold font-mono text-brand-cyan">
                        ¥{Number(detail.commission_stats.l1_total || 0).toFixed(2)}
                      </div>
                      <div className="text-[9px] text-ink-3 mt-0.5">
                        {detail.commission_stats.l1_count || 0} 笔
                      </div>
                    </div>
                    <div className="bg-bg-base rounded-lg p-2 text-center">
                      <div className="text-[10px] text-brand-purple">L2 间推</div>
                      <div className="text-sm font-bold font-mono text-brand-purple">
                        ¥{Number(detail.commission_stats.l2_total || 0).toFixed(2)}
                      </div>
                      <div className="text-[9px] text-ink-3 mt-0.5">
                        {detail.commission_stats.l2_count || 0} 笔
                      </div>
                    </div>
                    <div className="bg-bg-base rounded-lg p-2 text-center">
                      <div className="text-[10px] text-brand-gold">L3 三推</div>
                      <div className="text-sm font-bold font-mono text-brand-gold">
                        ¥{Number(detail.commission_stats.l3_total || 0).toFixed(2)}
                      </div>
                      <div className="text-[9px] text-ink-3 mt-0.5">
                        {detail.commission_stats.l3_count || 0} 笔
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs py-1.5 px-2 bg-accent-success/10 rounded-lg">
                    <span className="text-ink-2">累计到手</span>
                    <span className="text-accent-success font-mono font-bold">
                      ¥{Number(user.total_commission || 0).toFixed(2)}
                    </span>
                  </div>
                </Section>
              )}

              {/* 推荐关系 */}
              {(detail.ancestors?.length > 0 || detail.descendants?.length > 0) && (
                <Section title="推荐关系">
                  {detail.ancestors?.length > 0 && (
                    <div className="mb-2">
                      <div className="text-[10px] text-ink-3 mb-1">我的上级</div>
                      <div className="space-y-1">
                        {detail.ancestors.map(a => (
                          <div key={`a${a.level}`} className="flex items-center gap-2 text-xs bg-bg-base rounded px-2 py-1">
                            <span className={clsx(
                              'inline-block px-1.5 rounded text-[9px] font-mono',
                              a.level === 1 ? 'bg-brand-cyan/20 text-brand-cyan' :
                              a.level === 2 ? 'bg-brand-purple/20 text-brand-purple' :
                              'bg-brand-gold/20 text-brand-gold'
                            )}>
                              L{a.level}
                            </span>
                            <span className="font-mono text-ink-2 flex-1">{a.phone}</span>
                            <span className="text-ink-3">{a.name || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {detail.descendants?.length > 0 && (
                    <div>
                      <div className="text-[10px] text-ink-3 mb-1">
                        我的下级 ({detail.descendants.length})
                      </div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {detail.descendants.slice(0, 10).map(d => (
                          <div key={`d${d.level}-${d.id}`} className="flex items-center gap-2 text-xs bg-bg-base rounded px-2 py-1">
                            <span className={clsx(
                              'inline-block px-1.5 rounded text-[9px] font-mono',
                              d.level === 1 ? 'bg-brand-cyan/20 text-brand-cyan' :
                              d.level === 2 ? 'bg-brand-purple/20 text-brand-purple' :
                              'bg-brand-gold/20 text-brand-gold'
                            )}>
                              L{d.level}
                            </span>
                            <span className="font-mono text-ink-2 flex-1">{d.phone}</span>
                            <span className="text-ink-3">{d.name || '-'}</span>
                          </div>
                        ))}
                        {detail.descendants.length > 10 && (
                          <div className="text-[10px] text-ink-3 text-center py-1">
                            还有 {detail.descendants.length - 10} 个...
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {/* 佣金收到明细 */}
              {detail.commissions_received?.length > 0 && (
                <Section title={`佣金收到 (${detail.commissions_received.length})`}>
                  <div className="space-y-1.5">
                    {detail.commissions_received.slice(0, 10).map(c => (
                      <div key={c.id} className="flex items-center justify-between text-xs py-1 bg-bg-base rounded px-2">
                        <div className="flex items-center gap-1.5">
                          <span className={clsx(
                            'px-1 rounded text-[9px] font-mono',
                            c.level === 1 ? 'bg-brand-cyan/20 text-brand-cyan' :
                            c.level === 2 ? 'bg-brand-purple/20 text-brand-purple' :
                            'bg-brand-gold/20 text-brand-gold'
                          )}>
                            L{c.level}
                          </span>
                          <span className="text-ink-2">来自 {c.from_name || c.from_phone}</span>
                        </div>
                        <span className="text-accent-success font-mono text-[11px] font-semibold">
                          +¥{Number(c.amount).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* 充值记录 */}
              {detail.payments?.length > 0 && (
                <Section title={`充值记录 (${detail.payments.length})`}>
                  <div className="space-y-2">
                    {detail.payments.slice(0, 10).map(p => (
                      <div key={p.id} className="flex items-center justify-between text-xs py-1.5">
                        <div>
                          <div className="font-medium">{p.tier}</div>
                          <div className="text-[10px] text-ink-3">
                            {p.method} · {new Date(p.paid_at).toLocaleDateString('zh-CN')}
                          </div>
                        </div>
                        <div className="text-brand-gold font-mono font-semibold">¥{p.amount}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* 登录日志 */}
              {detail.logins?.length > 0 && (
                <Section title={`登录日志 (${detail.logins.length})`}>
                  <div className="space-y-1.5">
                    {detail.logins.slice(0, 10).map(l => (
                      <div key={l.id} className="flex items-center justify-between text-xs py-1">
                        <div className="flex items-center gap-1.5">
                          <div className={clsx('w-1.5 h-1.5 rounded-full', l.success ? 'bg-brand-cyan' : 'bg-accent-danger')} />
                          <span className="font-mono text-ink-2">{l.ip}</span>
                        </div>
                        <div className="text-[10px] text-ink-3">
                          {new Date(l.at).toLocaleString('zh-CN', { hour12: false })}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-ink-3 mb-2 uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-xs py-1.5">
      <div className="flex items-center gap-1.5 text-ink-3">
        <Icon size={12} />
        {label}
      </div>
      <div className="text-ink-1 font-mono">{value}</div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [tier, setTier] = useState(user.tier);
  const [status, setStatus] = useState(user.status);
  const [notes, setNotes] = useState(user.notes || '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.admin.updateUser(user.id, { tier, status, notes });
      toast.success('已保存');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title={`编辑用户 #${user.id}`}>
      <div className="text-sm text-ink-3 mb-3">{user.name} · {user.phone}</div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-ink-3 mb-1 block">会员档</label>
          <select value={tier} onChange={e => setTier(e.target.value)}
            className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm">
            <option>Pro 月付</option>
            <option>Pro 年付</option>
            <option>MAX 买断</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-ink-3 mb-1 block">状态</label>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm">
            <option value="active">active</option>
            <option value="disabled">disabled</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-ink-3 mb-1 block">备注</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm" />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={onClose} className="flex-1 btn-ghost text-sm">取消</button>
        <button onClick={save} disabled={saving} className="flex-1 btn-primary text-sm">
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </Modal>
  );
}

function AddPaymentModal({ user, onClose, onSaved }) {
  const [tier, setTier] = useState('Pro 月付');
  const [amount, setAmount] = useState(169);
  const [duration, setDuration] = useState(30);
  const [method, setMethod] = useState('manual');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // 方案默认金额
  const TIER_AMOUNT = {
    'Pro 月付': 169,
    'Pro 年付': 699,
    'MAX 买断': 1999,
  };
  const TIER_DURATION = {
    'Pro 月付': 30,
    'Pro 年付': 365,
    'MAX 买断': null,
  };

  function pickTier(t) {
    setTier(t);
    setAmount(TIER_AMOUNT[t]);
    setDuration(TIER_DURATION[t]);
  }

  async function save() {
    if (!confirm(`确认补单?\n\n用户: ${user.name}\n方案: ${tier}\n金额: ¥${amount}\n时长: ${duration ? duration + ' 天' : '永久'}`)) return;
    setSaving(true);
    try {
      await api.admin.createPayment({
        userId: user.id,
        amount,
        tier,
        durationDays: duration,
        method,
        notes,
      });
      toast.success('补单成功');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title="手动补单">
      <div className="text-sm text-ink-3 mb-3">
        为 <span className="text-ink-1 font-medium">{user.name}</span> 创建充值记录
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-ink-3 mb-1 block">选择方案</label>
          <div className="grid grid-cols-3 gap-1.5">
            {['Pro 月付', 'Pro 年付', 'MAX 买断'].map(t => (
              <button
                key={t}
                onClick={() => pickTier(t)}
                className={clsx(
                  'px-2 py-2 rounded-lg text-xs border transition',
                  tier === t
                    ? 'border-brand-cyan bg-brand-cyan/10 text-brand-cyan'
                    : 'border-white/10 text-ink-2 hover:border-white/20'
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-ink-3 mb-1 block">金额 (元)</label>
            <input type="number" value={amount} onChange={e => setAmount(+e.target.value)}
              className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm font-mono" />
          </div>
          <div>
            <label className="text-xs text-ink-3 mb-1 block">时长 (天)</label>
            <input type="number" value={duration || ''} onChange={e => setDuration(+e.target.value || null)}
              placeholder="永久" disabled={duration === null}
              className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm font-mono" />
          </div>
        </div>
        <div>
          <label className="text-xs text-ink-3 mb-1 block">支付方式</label>
          <select value={method} onChange={e => setMethod(e.target.value)}
            className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm">
            <option value="manual">人工补单</option>
            <option value="wechat">微信支付</option>
            <option value="alipay">支付宝</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-ink-3 mb-1 block">备注</label>
          <input value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="例:客服赠送 / 补单"
            className="w-full px-3 py-2 bg-bg-base border border-white/10 rounded-lg text-sm" />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={onClose} className="flex-1 btn-ghost text-sm">取消</button>
        <button onClick={save} disabled={saving} className="flex-1 btn-primary text-sm">
          {saving ? '提交中...' : '确认补单'}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} className="fixed inset-0 bg-black/60 z-50"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
      >
        <div className="card max-w-md w-full pointer-events-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold">{title}</h3>
            <button onClick={onClose} className="text-ink-3 hover:text-ink-1">
              <X size={18} />
            </button>
          </div>
          {children}
        </div>
      </motion.div>
    </>
  );
}

// 时钟占位(让 Clock 引用)
import { Clock } from 'lucide-react';
